"use client";

import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { ParsedAddress } from "@/components/inputs/address-autocomplete";
import { VerifiedAddressField } from "@/components/inputs/verified-address-field";
import {
  isValidUsZip,
  lookupPostalCode,
  type DeliveryLocation,
} from "@/lib/checkout/delivery-location";

interface PickupLocationFieldsProps {
  idPrefix: string;
  location: DeliveryLocation;
  setLocation: Dispatch<SetStateAction<DeliveryLocation>>;
}

const US_ONLY = ["us"];

function usePickupLocation(
  setLocation: PickupLocationFieldsProps["setLocation"],
) {
  const [lookup, setLookup] = useState<"idle" | "looking" | "failed">("idle");
  const lookupRequest = useRef(0);

  const handleQuery = (query: string) => {
    const zip = query.trim();
    const request = ++lookupRequest.current;
    if (!isValidUsZip(zip)) {
      setLookup("idle");
      return;
    }
    setLookup("looking");
    void lookupPostalCode(zip).then((result) => {
      if (lookupRequest.current !== request) return;
      if (!result) {
        setLookup("failed");
        return;
      }
      setLookup("idle");
      setLocation({ zip, city: result.city, state: result.state });
    });
  };

  const handlePick = (parsed: ParsedAddress) => {
    lookupRequest.current += 1;
    if (!isValidUsZip(parsed.zip)) {
      setLookup("failed");
      return;
    }
    setLookup("idle");
    setLocation((current) => ({
      zip: parsed.zip,
      city: parsed.city,
      state: parsed.state,
      line1: parsed.line1 || undefined,
      line2: parsed.line2 || current.line2,
    }));
  };

  return { lookup, handleQuery, handlePick };
}

function pickedLocation(location: DeliveryLocation) {
  if (!(isValidUsZip(location.zip) && location.city && location.state)) {
    return null;
  }
  return {
    line1: location.line1,
    line2: location.line2,
    city: location.city,
    state: location.state,
    postalCode: location.zip,
  };
}

export function PickupLocationFields({
  idPrefix,
  location,
  setLocation,
}: PickupLocationFieldsProps) {
  const { lookup, handleQuery, handlePick } = usePickupLocation(setLocation);

  return (
    <VerifiedAddressField
      id={`${idPrefix}-address`}
      label="Pickup address"
      value={pickedLocation(location)}
      onPick={handlePick}
      onLine2Change={(line2) =>
        setLocation((current) => ({ ...current, line2 }))
      }
      onQueryChange={handleQuery}
      placeholder="Search for the pickup address or ZIP code"
      regionCodes={US_ONLY}
      hint="Pick an address from the list or enter a 5-digit ZIP."
      error={
        lookup === "failed"
          ? "We couldn't confirm that location. Pick a US address from the list or enter a 5-digit ZIP code."
          : undefined
      }
      status={
        lookup === "looking" ? (
          <p role="status" className="text-sm text-muted-foreground">
            Looking up your ZIP code…
          </p>
        ) : null
      }
    />
  );
}
